import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Locale = 'en' | 'es';

/**
 * UI string catalog. Values may contain `{name}`-style placeholders filled via
 * the second `t()` argument. Every key in `en` must also exist in `es`.
 */
const MESSAGES = {
  en: {
    module: 'Module',
    role: 'Role',
    event: 'Event',
    language: 'Language',
    openMenu: 'Open menu',
    // Dashboard
    welcomeBack: 'Welcome back, {name}',
    assigned: 'Assigned',
    dueToday: 'Due today',
    awaiting: 'Awaiting',
    openCases: 'Open Cases',
    activeCaseFiles: 'Active case files',
    followUpsDue: 'Follow-Ups Due',
    dueTodayCount: '{n} due today',
    recoveryGoals: 'Recovery Goals',
    inProgressCount: '{n} in progress',
    openReferrals: 'Open Referrals',
    awaitingOutcomeCount: '{n} awaiting outcome',
    caseStatusBreakdown: 'Case Status Breakdown',
    tierDistribution: 'Tier Distribution',
    recentActivity: 'Recent Activity',
    upcomingFollowUps: 'Upcoming Follow-Ups',
    needsAttention: 'Needs Attention',
    viewAll: 'View All',
    dueCount: '{n} due',
    itemsCount: '{n} items',
    // Call Center
    callCenter: 'Call Center',
    callerLookup: 'Caller Lookup',
    logCall: 'Log Call',
    logNewCall: 'Log New Call',
    recentCallLog: 'Recent Call Log',
    callerName: 'Caller Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone',
    email: 'Email',
    reasonForCall: 'Reason for Call',
    callNotes: 'Call Notes',
    saveContactOnly: 'Save Contact Only',
    registerAsClient: 'Register as Client',
    goToClientRecord: 'Go to Client Record',
    convertToCase: 'Convert to Case',
    callHistory: 'Call History',
    noMatchFound: 'No match found — fill in the form below for a new caller.',
    searchCallerPlaceholder: 'Search by name, phone, email, or client ID...',
    contactOnly: 'Contact Only',
    registered: 'Registered',
    referred: 'Referred',
    handledBy: 'Handled By',
    outcome: 'Outcome',
    totalCalls: 'Total Calls',
    firstContact: 'First Contact',
    lastContact: 'Last Contact',
    callNoteHistory: 'Call Notes',
    noReferral: 'No referral',
    referralOptional: 'Referral (optional)',
    // Nav items
    dashboard: 'Dashboard',
    newClientRecord: 'New Client Record',
    clientSearch: 'Client Search',
    manageMyCases: 'Manage My Cases',
    resourceLibrary: 'Resource Library',
    admin: 'Admin',
    // Common table headers & labels
    date: 'Date',
    name: 'Name',
    case_: 'Case',
    reason: 'Reason',
    notes: 'Notes',
    status: 'Status',
    actions: 'Actions',
    relationship: 'Relationship',
    dateOfBirth: 'Date of Birth',
    age: 'Age',
    condition: 'Condition',
    race: 'Race',
    ethnicity: 'Ethnicity',
    county: 'County',
    address: 'Street Address',
    city: 'City',
    state: 'State',
    zip: 'ZIP',
    // Client Record
    clientRecord: 'Client Record',
    disasterCases: 'Cases',
    householdMembers: 'Household Members',
    dateOfBirthShort: 'DOB',
    currentAddress: 'Current Address',
    managingOrganization: 'Managing Organization',
    preferredLanguage: 'Preferred Language',
    bestTimeToContact: 'Best Time to Contact',
    flags: 'Flags',
    backToSearch: 'Back to search',
    print: 'Print',
    noCallHistory: 'No call history.',
    noCasesOnRecord: 'No cases on record.',
    noHouseholdMembers: 'No household members on record.',
    // Case Record sections
    clientInformation: 'Client information',
    initialIntakeInformation: 'Initial intake information',
    householdIncome: 'Household income',
    releaseAndConsent: 'Release and consent',
    registrantSignature: 'Registrant signature',
    // Case Record field labels
    middleName: 'Middle Name',
    nickname: 'Nickname / Other Name',
    gender: 'Gender',
    phoneNumber: 'Phone Number',
    alternatePhone: 'Alternate Phone',
    coApplicantName: 'Co-applicant Name',
    coApplicantPhone: 'Co-applicant Phone',
    coApplicantRelationship: 'Co-applicant Relationship',
    headOfHousehold: 'Head of Household',
    dateOfDcmIntake: 'Date of DCM Intake',
    whoReferredClient: 'Who Referred the Client',
    otherReferralSource: 'Other Referral Source',
    dateOfArrival: 'Date of Arrival',
    contactInitiatedBy: 'Contact Initiated By',
    intakeOrganization: 'Intake Organization',
    intakeLocation: 'Intake Location',
    clientRecoveryStatus: 'Client Recovery Status',
    femaNumber: 'VA File Number',
    grossMonthlyIncome: 'Gross Monthly Household Income',
    monthlyExpenses: 'Monthly Household Expenses',
    addMember: 'Add member',
    editMember: 'Edit household member',
    addHouseholdMember: 'Add household member',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    shelterServiceNeeds: 'Shelter Service Needs',
    // Release & consent
    releaseOfInfoVerified: 'Release of Information verified',
    femaReleaseUploaded: 'VA Release uploaded',
    rightsResponsibilities: 'Rights & Responsibilities uploaded',
    verbalConsent: 'Applicant verbal consent recorded',
    authToShare: 'Authorization to share',
    ninetyDayNotice: '90-day notice',
    orgOptOut: 'Organization opt-out',
    revokingConsent: 'Revoking consent',
    verifyRelease: 'Verify Release of Information',
    iVerify: 'I verify',
    // iSTAT
    istatDamageAssessment: 'iSTAT Damage Assessment',
    istatIncident: 'VSO Incident',
    launchIstat: 'Launch iSTAT',
    launchIstatNewTab: 'Launch iSTAT in new tab',
    selectIncident: 'Select incident...',
    // Call Center extras
    linkToCase: 'Link to Case',
    select: 'Select',
    clear: 'Clear',
    search: 'Search',
    noPreviousCalls: 'No previous calls on record.',
    searchForCaller: 'Search for a caller above to see their call history.',
    noCallLogEntries: 'No call log entries.',
    noNotes: 'No notes.',
    fullNotes: 'Full Notes',
  },
  es: {
    module: 'Módulo',
    role: 'Rol',
    event: 'Evento',
    language: 'Idioma',
    openMenu: 'Abrir menú',
    // Dashboard
    welcomeBack: 'Bienvenido de nuevo, {name}',
    assigned: 'Asignados',
    dueToday: 'Vencen hoy',
    awaiting: 'Pendientes',
    openCases: 'Casos abiertos',
    activeCaseFiles: 'Expedientes activos',
    followUpsDue: 'Seguimientos pendientes',
    dueTodayCount: '{n} vencen hoy',
    recoveryGoals: 'Metas de recuperación',
    inProgressCount: '{n} en progreso',
    openReferrals: 'Referencias abiertas',
    awaitingOutcomeCount: '{n} esperando resultado',
    caseStatusBreakdown: 'Desglose por estado de caso',
    tierDistribution: 'Distribución por nivel',
    recentActivity: 'Actividad reciente',
    upcomingFollowUps: 'Próximos seguimientos',
    needsAttention: 'Requiere atención',
    viewAll: 'Ver todo',
    dueCount: '{n} pendientes',
    itemsCount: '{n} elementos',
    // Call Center
    callCenter: 'Centro de Llamadas',
    callerLookup: 'Buscar Persona',
    logCall: 'Registrar Llamada',
    logNewCall: 'Registrar Nueva Llamada',
    recentCallLog: 'Registro de Llamadas Recientes',
    callerName: 'Nombre de la Persona',
    firstName: 'Nombre',
    lastName: 'Apellido',
    phone: 'Teléfono',
    email: 'Correo Electrónico',
    reasonForCall: 'Motivo de la Llamada',
    callNotes: 'Notas de la Llamada',
    saveContactOnly: 'Guardar Solo Contacto',
    registerAsClient: 'Registrar como Cliente',
    goToClientRecord: 'Ir al Registro del Cliente',
    convertToCase: 'Convertir a Caso',
    callHistory: 'Historial de Llamadas',
    noMatchFound: 'No se encontró coincidencia — complete el formulario a continuación.',
    searchCallerPlaceholder: 'Buscar por nombre, teléfono, correo o ID de cliente...',
    contactOnly: 'Solo Contacto',
    registered: 'Registrado',
    referred: 'Referido',
    handledBy: 'Atendido Por',
    outcome: 'Resultado',
    totalCalls: 'Total de Llamadas',
    firstContact: 'Primer Contacto',
    lastContact: 'Último Contacto',
    callNoteHistory: 'Notas de Llamadas',
    noReferral: 'Sin referencia',
    referralOptional: 'Referencia (opcional)',
    // Nav items
    dashboard: 'Panel',
    newClientRecord: 'Nuevo Registro de Cliente',
    clientSearch: 'Búsqueda de Clientes',
    manageMyCases: 'Administrar Mis Casos',
    resourceLibrary: 'Biblioteca de Recursos',
    admin: 'Administración',
    // Common table headers & labels
    date: 'Fecha',
    name: 'Nombre',
    case_: 'Caso',
    reason: 'Motivo',
    notes: 'Notas',
    status: 'Estado',
    actions: 'Acciones',
    relationship: 'Parentesco',
    dateOfBirth: 'Fecha de Nacimiento',
    age: 'Edad',
    condition: 'Condición',
    race: 'Raza',
    ethnicity: 'Etnia',
    county: 'Condado',
    address: 'Dirección',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Código Postal',
    // Client Record
    clientRecord: 'Registro del Cliente',
    disasterCases: 'Casos',
    householdMembers: 'Miembros del Hogar',
    dateOfBirthShort: 'Fecha Nac.',
    currentAddress: 'Dirección Actual',
    managingOrganization: 'Organización Administrativa',
    preferredLanguage: 'Idioma Preferido',
    bestTimeToContact: 'Mejor Hora de Contacto',
    flags: 'Indicadores',
    backToSearch: 'Volver a la búsqueda',
    print: 'Imprimir',
    noCallHistory: 'Sin historial de llamadas.',
    noCasesOnRecord: 'No hay casos registrados.',
    noHouseholdMembers: 'No hay miembros del hogar registrados.',
    // Case Record sections
    clientInformation: 'Información del cliente',
    initialIntakeInformation: 'Información de ingreso inicial',
    householdIncome: 'Ingresos del hogar',
    releaseAndConsent: 'Autorización y consentimiento',
    registrantSignature: 'Firma del registrante',
    // Case Record field labels
    middleName: 'Segundo Nombre',
    nickname: 'Apodo / Otro Nombre',
    gender: 'Género',
    phoneNumber: 'Número de Teléfono',
    alternatePhone: 'Teléfono Alternativo',
    coApplicantName: 'Nombre del Co-solicitante',
    coApplicantPhone: 'Teléfono del Co-solicitante',
    coApplicantRelationship: 'Parentesco del Co-solicitante',
    headOfHousehold: 'Jefe de Hogar',
    dateOfDcmIntake: 'Fecha de Ingreso DCM',
    whoReferredClient: 'Quién Refirió al Cliente',
    otherReferralSource: 'Otra Fuente de Referencia',
    dateOfArrival: 'Fecha de Llegada',
    contactInitiatedBy: 'Contacto Iniciado Por',
    intakeOrganization: 'Organización de Ingreso',
    intakeLocation: 'Ubicación de Ingreso',
    clientRecoveryStatus: 'Estado de Recuperación del Cliente',
    femaNumber: 'Número VA',
    grossMonthlyIncome: 'Ingreso Mensual Bruto del Hogar',
    monthlyExpenses: 'Gastos Mensuales del Hogar',
    addMember: 'Agregar miembro',
    editMember: 'Editar miembro del hogar',
    addHouseholdMember: 'Agregar miembro del hogar',
    saveChanges: 'Guardar cambios',
    cancel: 'Cancelar',
    shelterServiceNeeds: 'Necesidades de Servicio de Refugio',
    // Release & consent
    releaseOfInfoVerified: 'Autorización de información verificada',
    femaReleaseUploaded: 'Autorización VA cargada',
    rightsResponsibilities: 'Derechos y Responsabilidades cargados',
    verbalConsent: 'Consentimiento verbal del solicitante registrado',
    authToShare: 'Autorización para compartir',
    ninetyDayNotice: 'Aviso de 90 días',
    orgOptOut: 'Exclusión de la organización',
    revokingConsent: 'Revocación del consentimiento',
    verifyRelease: 'Verificar Autorización de Información',
    iVerify: 'Verifico',
    // iSTAT
    istatDamageAssessment: 'Evaluación de Daños iSTAT',
    istatIncident: 'Incidente VSO',
    launchIstat: 'Iniciar iSTAT',
    launchIstatNewTab: 'Iniciar iSTAT en nueva pestaña',
    selectIncident: 'Seleccionar incidente...',
    // Call Center extras
    linkToCase: 'Vincular a Caso',
    select: 'Seleccionar',
    clear: 'Limpiar',
    search: 'Buscar',
    noPreviousCalls: 'No hay llamadas previas registradas.',
    searchForCaller: 'Busque una persona arriba para ver su historial de llamadas.',
    noCallLogEntries: 'No hay entradas en el registro de llamadas.',
    noNotes: 'Sin notas.',
    fullNotes: 'Notas Completas',
  },
} as const;

export type MessageKey = keyof (typeof MESSAGES)['en'];

/**
 * Spanish translations for known *data* values (statuses, tiers, categories,
 * and the demo content shown in the UI). `tv()` returns the original string
 * when no translation exists, so untranslated data passes through unchanged.
 */
const DATA_ES: Record<string, string> = {
  // Case statuses
  Open: 'Abierto',
  Review: 'En revisión',
  Pending: 'Pendiente',
  Closed: 'Cerrado',
  'On Wait List': 'En lista de espera',
  // Tiers + cadence
  'Tier 1': 'Nivel 1',
  'Tier 2': 'Nivel 2',
  'Tier 3': 'Nivel 3',
  'Tier 4': 'Nivel 4',
  'Weekly contact': 'Contacto semanal',
  'Bi-weekly contact': 'Contacto quincenal',
  'Monthly contact': 'Contacto mensual',
  'Monthly monitoring': 'Monitoreo mensual',
  // Follow-up types + due labels
  Phone: 'Teléfono',
  'In-person': 'En persona',
  Overdue: 'Atrasado',
  Today: 'Hoy',
  // Recent activity
  'Recovery goal updated': 'Meta de recuperación actualizada',
  'Partner case submitted': 'Caso de socio enviado',
  'Disability referral created': 'Referencia por discapacidad creada',
  'Follow-up completed': 'Seguimiento completado',
  'Transportation referral added': 'Referencia de transporte agregada',
  // Needs attention (primary)
  '4 overdue follow-ups': '4 seguimientos atrasados',
  '3 expiring housing plans': '3 planes de vivienda por vencer',
  '5 referrals awaiting outcome': '5 referencias esperando resultado',
  '3 cases ready for review': '3 casos listos para revisión',
  // Needs attention (reason)
  'Clients not contacted past scheduled date':
    'Clientes no contactados después de la fecha programada',
  'Vouchers expiring within 30 days': 'Vales que vencen en 30 días',
  'Referrals made but not yet resolved': 'Referencias hechas pero aún no resueltas',
  'Ready for supervisory review': 'Listo para revisión del supervisor',
  // Call Center outcomes + reasons
  'Contact Only': 'Solo Contacto',
  Registered: 'Registrado',
  Referred: 'Referido',
  'Converted to Case': 'Convertido a Caso',
  'Information request': 'Solicitud de información',
  'Referral needed': 'Referencia necesaria',
  'Ready to register': 'Listo para registrarse',
  'Follow-up on previous call': 'Seguimiento de llamada anterior',
  Other: 'Otro',
  // Nav items
  'Call Center': 'Centro de Llamadas',
  'New Client Record': 'Nuevo Registro de Cliente',
  'Client Search': 'Búsqueda de Clientes',
  'Manage My Cases': 'Administrar Mis Casos',
  'Resource Library': 'Biblioteca de Recursos',
  Admin: 'Administración',
  Dashboard: 'Panel',
};

const STORAGE_KEY = 'tcr-locale';

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Translate a known UI key; supports `{token}` interpolation. */
  t: (key: MessageKey, params?: Record<string, string | number>) => string;
  /** Translate a known data value (falls back to the original string). */
  tv: (value: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  return window.localStorage.getItem(STORAGE_KEY) === 'es' ? 'es' : 'en';
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

export function LanguageProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  // Reflect the active locale on <html lang> for a11y / SonarQube S6858.
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => interpolate(MESSAGES[locale][key], params),
      tv: (val) => (locale === 'es' ? (DATA_ES[val] ?? val) : val),
    }),
    [locale, setLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
